import { useTranslation } from '@hbs-us/ui';

import { AssignmentCardActions } from './AssignmentCardActions.js';
import type { DispatchAssignmentDto } from './contractsMirror.js';

/**
 * The day / agenda list — the responsive variant shown below the `lg` breakpoint (UI-SPEC: a
 * 7-column drag board is not usable on a phone, so small viewports get a single-day agenda). The
 * board itself stays available above `lg`; this is a read + status-friendly list for narrow
 * screens. The drag affordance is intentionally absent here (touch-drag on a tiny grid is the worst
 * UX); scheduling on small screens goes through the same "Schedule…" fallback dialog the board
 * exposes (A11Y-01 — never pointer-only). Token-only — no hex.
 */

export function DispatchDayView({
  days,
  selectedDate,
  assignments,
  onSelectDay,
  onEditAssignment,
  onUnassignAssignment,
}: {
  days: string[];
  selectedDate: string;
  assignments: DispatchAssignmentDto[];
  onSelectDay: (date: string) => void;
  onEditAssignment: (assignment: DispatchAssignmentDto) => void;
  onUnassignAssignment: (assignment: DispatchAssignmentDto) => void;
}) {
  const { t } = useTranslation();
  const dayAssignments = assignments.filter((a) => a.scheduledDate === selectedDate);

  function dayLabel(date: string): string {
    const d = new Date(`${date}T00:00:00Z`);
    return d.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }

  return (
    <section aria-label={t('dispatch:dayView.heading')} className="flex flex-col gap-3 lg:hidden">
      {/* The day picker — a tablist of the 7 days; arrow keys + click both select. */}
      <div
        role="tablist"
        aria-label={t('dispatch:dayView.dayPicker')}
        className="flex gap-1 overflow-x-auto"
      >
        {days.map((date) => {
          const selected = date === selectedDate;
          return (
            <button
              key={date}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onSelectDay(date)}
              className={`shrink-0 rounded-md px-3 py-2 font-medium text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                selected
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-foreground hover:bg-secondary/60'
              }`}
            >
              {dayLabel(date)}
            </button>
          );
        })}
      </div>

      <ul className="flex flex-col gap-2">
        {dayAssignments.length === 0 ? (
          <li className="rounded-md border border-border border-dashed bg-card p-4 text-center text-muted-foreground text-sm">
            {t('dispatch:dayView.empty')}
          </li>
        ) : (
          dayAssignments.map((a) => (
            <li key={a.id} className="rounded-md border border-border bg-card p-3">
              <p className="font-medium text-foreground text-sm">{a.jobName ?? a.employeeName}</p>
              <p className="text-muted-foreground text-xs">{a.employeeName}</p>
              {a.startTime ? (
                <p className="text-muted-foreground text-xs">
                  {a.startTime}
                  {a.endTime ? `–${a.endTime}` : ''}
                </p>
              ) : null}
              {a.status ? (
                <p className="mt-1 text-muted-foreground text-xs">
                  {t(`dispatch:status.${a.status}`, { defaultValue: a.status })}
                </p>
              ) : null}
              <AssignmentCardActions
                assignment={a}
                onEdit={onEditAssignment}
                onUnassign={onUnassignAssignment}
              />
            </li>
          ))
        )}
      </ul>
    </section>
  );
}
